import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext.js';
import { ApiClient } from '../lib/api.js';

// Bare x.com/search URL. The (query → monitorId) mapping that lets the extension
// attribute scraped tweets is registered separately via registerPassiveCapture()
// just before we open the tab — X strips unknown URL params on landing.
function searchUrlFor(keyword: string): string {
  return `https://x.com/search?q=${encodeURIComponent(keyword)}&src=typed_query&f=live`;
}

// Sort the feed by overall score descending and drop anything below the user's threshold.
// Returns a new array; we recompute on each render — fine for hundreds of items.
function sortedFilteredPosts(posts: any[], minScore: number): any[] {
  return [...posts]
    .filter(p => (p?.scores?.overall ?? 0) >= minScore)
    .sort((a, b) => (b?.scores?.overall ?? 0) - (a?.scores?.overall ?? 0));
}

async function openKeywordSearch(monitorId: string, keyword: string): Promise<void> {
  try {
    const { registerPassiveCapture } = await import('../lib/extensionBridge.js');
    await registerPassiveCapture(keyword, monitorId);
  } catch { /* extension may not be installed — open the link anyway */ }
  window.open(searchUrlFor(keyword), '_blank', 'noopener');
}

/**
 * Standalone /app/radar route — keeps URL+sidebar back-compat. The UI now lives
 * inside Home's "Engage" tab; this route just renders the panel with page chrome.
 */
export function Radar() {
  return (
    <div className="page">
      <div className="page-head">
        <h1>Radar</h1>
        <p className="lede">
          Track topics you care about. We surface the highest-leverage conversations and draft replies in your voice.
        </p>
      </div>
      <EngagePanel />
    </div>
  );
}

/**
 * Engage panel: Topics card on top, Feed beneath. Each keyword in a topic is a
 * clickable link to x.com/search — the extension watches those tabs and
 * passively scrapes notable tweets into the feed.
 */
export function EngagePanel() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const api = new ApiClient(getToken);

  const [me, setMe] = useState<any>(null);
  const [monitors, setMonitors] = useState<any[]>([]);
  const [posts, setPosts] = useState<any[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  // Min-score filter — persisted so power users keep their preferred threshold.
  const MIN_SCORE_KEY = 'tv_engage_min_score';
  const [minScore, setMinScore] = useState<number>(() => {
    const raw = Number(localStorage.getItem(MIN_SCORE_KEY) ?? '60');
    return Number.isFinite(raw) ? Math.max(40, Math.min(95, raw)) : 60;
  });
  useEffect(() => { localStorage.setItem(MIN_SCORE_KEY, String(minScore)); }, [minScore]);

  useEffect(() => {
    (async () => {
      try {
        const [m, mons, feed] = await Promise.all([api.me(), api.monitors(), api.radar()]);
        setMe(m);
        setMonitors(mons.monitors ?? []);
        setPosts(feed.posts ?? []);
      } catch (e: any) { setErr(e.message); }
      setLoading(false);
    })();
    /* eslint-disable-next-line */
  }, []);

  // Light auto-refresh: when the user comes back to this tab after browsing X,
  // re-pull the feed so newly-scraped tweets show up.
  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible') refreshFeed();
    }
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    /* eslint-disable-next-line */
  }, []);

  async function refreshFeed() {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const r = await api.radar();
      setPosts(r.posts ?? []);
    } catch { /* ignore — manual refresh exists */ }
    setRefreshing(false);
  }

  async function reloadMonitors() {
    const r = await api.monitors();
    setMonitors(r.monitors ?? []);
  }

  const [hint, setHint] = useState<string>('');
  const [captureStatus, setCaptureStatus] = useState<{ ts: number; ok: boolean; status?: number; count?: number; reason?: string; query?: string } | null>(null);

  // Poll the extension for its last passive-capture result so users can see whether
  // browsing X is actually filling their feed (and surface auth issues we'd otherwise
  // swallow silently).
  useEffect(() => {
    let cancelled = false;
    async function poll() {
      try {
        const { readLastCaptureStatus } = await import('../lib/extensionBridge.js');
        const s = await readLastCaptureStatus();
        if (!cancelled) setCaptureStatus(s);
      } catch { /* extension not installed */ }
    }
    poll();
    const id = window.setInterval(poll, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  async function copyToClipboard(text: string, hintMsg?: string): Promise<void> {
    try { await navigator.clipboard.writeText(text); } catch { /* ignore */ }
    if (hintMsg) { setHint(hintMsg); setTimeout(() => setHint(''), 1500); }
  }

  function tweetUrlFor(p: any): string | null {
    if (p.tweetUrl) return p.tweetUrl;
    if (p.author?.handle && p.tweetId && /^\d+$/.test(p.tweetId)) {
      return `https://x.com/${p.author.handle}/status/${p.tweetId}`;
    }
    return null;
  }

  async function markStatus(tweetId: string, status: string) {
    if (status === 'ignored' || status === 'replied' || status === 'bookmarked') {
      setPosts(prev => prev.filter(p => p.tweetId !== tweetId));
    }
    try { await api.setRadarStatus(tweetId, status); }
    catch (e: any) { setErr(`Couldn't update status: ${e.message}`); }
  }

  async function actReply(p: any) {
    if (p.draftReply) await copyToClipboard(p.draftReply, '✓ Reply copied — click Reply on the tweet, then paste.');
    const url = tweetUrlFor(p);
    if (url) window.open(url, '_blank', 'noopener');
    await markStatus(p.tweetId, 'replied');
  }
  async function actLike(p: any) {
    const url = tweetUrlFor(p);
    if (url) window.open(url, '_blank', 'noopener');
    await markStatus(p.tweetId, 'bookmarked');
  }
  async function actQuote(p: any) {
    const url = tweetUrlFor(p);
    const draft = p.draftReply ?? '';
    const intent = url
      ? `https://x.com/intent/tweet?text=${encodeURIComponent(draft)}&url=${encodeURIComponent(url)}`
      : `https://x.com/intent/tweet?text=${encodeURIComponent(draft + (url ? '\n' + url : ''))}`;
    window.open(intent, '_blank', 'noopener');
    await markStatus(p.tweetId, 'replied');
  }
  async function actDismiss(p: any) { await markStatus(p.tweetId, 'ignored'); }

  const ent = me?.entitlements;
  const isPaid = ent && ent.maxKeywordMonitors > 0;

  // Wait until /v1/me lands before we render — otherwise free vs paid flickers
  // (PaywallCard for an instant, then the real topics editor).
  if (loading) {
    return <div className="loading">Loading…</div>;
  }

  return (
    <>
      {err && <div className="error">{err}</div>}
      {hint && <div className="status-row"><span className="status-icon">✓</span><span>{hint}</span></div>}

      {/* Topics card — always on top. */}
      <TopicsTab
        isPaid={!!isPaid}
        monitors={monitors}
        api={api}
        onChange={reloadMonitors}
        onUpgrade={() => navigate('/app/billing')}
      />

      {captureStatus && <CaptureStatusPill s={captureStatus} />}

      {/* Feed beneath. */}
      <div className="card">
        <div className="button-row between" style={{ marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>Feed</h3>
          <button onClick={refreshFeed} disabled={refreshing}>{refreshing ? 'Refreshing…' : 'Refresh'}</button>
        </div>

        {posts.length > 0 && (
          <div className="button-row between" style={{ marginBottom: 12, flexWrap: 'wrap', gap: 12 }}>
            <label className="muted small" style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 220 }}>
              <span>Min priority:</span>
              <input
                type="range"
                min={40}
                max={95}
                step={5}
                value={minScore}
                onChange={e => setMinScore(Number(e.target.value))}
                style={{ flex: 1 }}
              />
              <span style={{ minWidth: 28, textAlign: 'right' }}>{minScore}</span>
            </label>
            <span className="muted small">
              Showing {sortedFilteredPosts(posts, minScore).length} of {posts.length}
            </span>
          </div>
        )}

        {posts.length === 0 ? (
          <div className="empty" style={{ padding: '24px 8px' }}>
            <p className="muted" style={{ margin: 0 }}>Feed is empty.</p>
            <p className="helper" style={{ marginTop: 8 }}>
              Click a keyword above to open X search. As you scroll, our extension captures notable tweets and compiles them here.
            </p>
          </div>
        ) : sortedFilteredPosts(posts, minScore).length === 0 ? (
          <div className="empty" style={{ padding: '24px 8px' }}>
            <p className="muted" style={{ margin: 0 }}>No posts above score {minScore}.</p>
            <p className="helper" style={{ marginTop: 8 }}>Drop the slider to see lower-priority items.</p>
          </div>
        ) : (
          <div className="feed">
            {sortedFilteredPosts(posts, minScore).map(p => {
              const url = tweetUrlFor(p);
              return (
                <article key={p.tweetId} className="card">
                  <header>
                    <strong>{url ? <a href={url} target="_blank" rel="noopener noreferrer">@{p.author?.handle}</a> : `@${p.author?.handle}`}</strong>
                    <span className={`badge action-${p.recommendedAction}`}>{p.recommendedAction.replace(/_/g, ' ')}</span>
                    <span className="muted small" style={{ marginLeft: 'auto' }}>score {p.scores?.overall}</span>
                  </header>
                  <p className="post-text">{p.text}</p>
                  {p.draftReply && (
                    <div className="reply">
                      <div className="label">Draft reply</div>
                      <p style={{ margin: '4px 0 10px', lineHeight: 1.55 }}>{p.draftReply}</p>
                      <div className="button-row end">
                        <button onClick={() => copyToClipboard(p.draftReply, '✓ Reply copied')}>Copy reply</button>
                      </div>
                    </div>
                  )}
                  <div className="button-row" style={{ marginTop: 12 }}>
                    <button className="primary" onClick={() => actReply(p)} disabled={!url}>Reply on X →</button>
                    <button onClick={() => actQuote(p)} disabled={!url}>Quote</button>
                    <button onClick={() => actLike(p)} disabled={!url}>Open / like</button>
                    <button className="ghost" onClick={() => actDismiss(p)} style={{ marginLeft: 'auto' }}>Dismiss</button>
                  </div>
                  {!url && <p className="helper" style={{ marginTop: 6 }}>No permalink scraped — can't open in X.</p>}
                </article>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

// Tiny diagnostic pill: shows the most recent passive-capture attempt the extension
// made, so the user can tell whether scrolling X is actually populating the feed.
function CaptureStatusPill({ s }: { s: { ts: number; ok: boolean; status?: number; count?: number; reason?: string; query?: string } }) {
  const ago = Math.max(0, Math.round((Date.now() - s.ts) / 1000));
  const agoLabel = ago < 60 ? `${ago}s ago` : ago < 3600 ? `${Math.round(ago / 60)}m ago` : `${Math.round(ago / 3600)}h ago`;
  let msg = '';
  let kind: 'ok' | 'warn' | 'bad' = 'ok';
  if (s.ok) {
    msg = `Captured ${s.count ?? 0} tweet${s.count === 1 ? '' : 's'}${s.query ? ` for "${s.query}"` : ''} · ${agoLabel}`;
  } else if (s.status === 401 || s.reason === 'no_token') {
    msg = `Extension can't reach the API — your session expired. Reload tweetvia.com to refresh the connection.`;
    kind = 'bad';
  } else if (s.status && s.status >= 400) {
    msg = `Last capture failed (HTTP ${s.status})${s.query ? ` for "${s.query}"` : ''} · ${agoLabel}`;
    kind = 'warn';
  } else {
    msg = `Last capture: ${s.reason ?? 'failed'}${s.query ? ` for "${s.query}"` : ''} · ${agoLabel}`;
    kind = 'warn';
  }
  const color = kind === 'bad' ? 'var(--bad)' : kind === 'warn' ? 'var(--warning, #b45309)' : 'var(--muted)';
  return (
    <div className="muted small" style={{ padding: '4px 2px', color }}>
      {msg}
    </div>
  );
}

// ---------- Topics tab ----------
function TopicsTab({ isPaid, monitors, api, onChange, onUpgrade }: {
  isPaid: boolean;
  monitors: any[];
  api: ApiClient;
  onChange: () => Promise<void>;
  onUpgrade: () => void;
}) {
  if (!isPaid) {
    return <PaywallCard onUpgrade={onUpgrade} />;
  }
  return (
    <>
      <AddTopicForm api={api} onAdded={onChange} />
      {monitors.length > 0 && (
        <div className="card">
          <h3>Tracking</h3>
          <div className="stack">
            {monitors.map(m => (
              <TopicCard key={m.monitorId} monitor={m} api={api} onChanged={onChange} />
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function PaywallCard({ onUpgrade }: { onUpgrade: () => void }) {
  return (
    <div className="card paywall">
      <div className="paywall-badge">Creator feature</div>
      <h3>Track topics with AI keyword expansion</h3>
      <p className="muted">
        Type any topic — "AI agents", "founder content", "growth marketing" — and we turn it into
        a tuned set of X search keywords. We scan continuously and surface the highest-leverage conversations
        to engage with, with reply drafts in your voice.
      </p>
      <ul className="bullet-list" style={{ marginTop: 12 }}>
        <li>Up to 10 tracked topics on Creator</li>
        <li>Daily radar scans</li>
        <li>Auto-generated reply drafts for top candidates</li>
      </ul>
      <div className="button-row" style={{ marginTop: 16 }}>
        <button className="primary" onClick={onUpgrade}>Upgrade to Creator →</button>
      </div>
    </div>
  );
}

function AddTopicForm({ api, onAdded }: { api: ApiClient; onAdded: () => Promise<void> }) {
  const [topic, setTopic] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ keywords: string[]; rationale: string; topic: string } | null>(null);

  async function expand() {
    if (!topic.trim()) return;
    setBusy(true); setErr(null); setPreview(null);
    try {
      const r = await api.expandTopic(topic.trim());
      setPreview({ keywords: r.keywords, rationale: r.rationale, topic: r.topic });
    } catch (e: any) { setErr(e.message); }
    setBusy(false);
  }

  function removeKeyword(i: number) {
    if (!preview) return;
    setPreview({ ...preview, keywords: preview.keywords.filter((_, idx) => idx !== i) });
  }
  function addKeyword(k: string) {
    if (!preview || !k.trim()) return;
    setPreview({ ...preview, keywords: [...preview.keywords, k.trim()] });
  }

  async function save() {
    if (!preview) return;
    setBusy(true); setErr(null);
    try {
      await api.createMonitor({ name: preview.topic, topic: preview.topic, keywords: preview.keywords });
      setTopic(''); setPreview(null);
      await onAdded();
    } catch (e: any) { setErr(e.message); }
    setBusy(false);
  }

  return (
    <div className="card">
      <h3>Track a topic</h3>
      <p className="muted small" style={{ margin: '0 0 12px' }}>
        Type a topic in plain English. We turn it into searchable keywords.
      </p>

      <div className="field">
        <div className="row" style={{ margin: 0 }}>
          <input
            value={topic}
            onChange={e => setTopic(e.target.value)}
            placeholder="e.g. AI agents, founder content, growth marketing"
            onKeyDown={e => { if (e.key === 'Enter' && !busy) expand(); }}
            style={{ flex: 1 }}
            disabled={busy}
          />
          <button onClick={expand} disabled={busy || !topic.trim()}>
            {busy && !preview ? 'Expanding…' : 'Expand'}
          </button>
        </div>
      </div>

      {err && <div className="error">{err}</div>}

      {preview && (
        <>
          <p className="muted small" style={{ margin: '12px 0 6px' }}>
            <strong style={{ color: 'var(--text)' }}>Suggested keywords</strong> — edit, remove, or add your own:
          </p>
          <KeywordChips
            keywords={preview.keywords}
            onRemove={removeKeyword}
            onAdd={addKeyword}
            editable
          />
          {preview.rationale && <p className="helper" style={{ marginTop: 8 }}>{preview.rationale}</p>}
          <div className="button-row end" style={{ marginTop: 12 }}>
            <button className="ghost" onClick={() => { setPreview(null); setTopic(''); }}>Cancel</button>
            <button className="primary" onClick={save} disabled={busy || preview.keywords.length === 0}>
              {busy ? 'Saving…' : `Track "${preview.topic}"`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function TopicCard({ monitor, api, onChanged }: { monitor: any; api: ApiClient; onChanged: () => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [keywords, setKeywords] = useState<string[]>(monitor.keywords ?? []);

  async function save() {
    await api.updateMonitor(monitor.monitorId, { keywords });
    setEditing(false);
    await onChanged();
  }
  async function remove() {
    await api.deleteMonitor(monitor.monitorId);
    await onChanged();
  }

  return (
    <div className="topic-card">
      <div className="button-row between">
        <strong>{monitor.topic ?? monitor.name}</strong>
        <div className="button-row" style={{ margin: 0 }}>
          {editing ? (
            <>
              <button className="ghost" onClick={() => { setKeywords(monitor.keywords ?? []); setEditing(false); }}>Cancel</button>
              <button className="primary" onClick={save}>Save</button>
            </>
          ) : (
            <>
              <button className="ghost" onClick={() => setEditing(true)}>Edit</button>
              <button className="ghost" onClick={remove}>Delete</button>
            </>
          )}
        </div>
      </div>
      <KeywordChips
        keywords={editing ? keywords : (monitor.keywords ?? [])}
        onRemove={editing ? (i) => setKeywords(keywords.filter((_, idx) => idx !== i)) : undefined}
        onAdd={editing ? (k) => setKeywords([...keywords, k]) : undefined}
        editable={editing}
        searchHrefFor={editing ? undefined : (k) => searchUrlFor(k)}
        onSearchClick={editing ? undefined : (k) => openKeywordSearch(monitor.monitorId, k)}
      />
      {!editing && (
        <p className="helper" style={{ marginTop: 8 }}>
          Click a keyword to open X search. As you scroll, our extension captures notable tweets into your feed below.
        </p>
      )}
    </div>
  );
}

function KeywordChips({ keywords, onRemove, onAdd, editable, searchHrefFor, onSearchClick }: {
  keywords: string[];
  onRemove?: (i: number) => void;
  onAdd?: (k: string) => void;
  editable: boolean;
  /** If provided (non-editing mode), each keyword renders as a link to this URL. */
  searchHrefFor?: (k: string) => string;
  /** Called on click of a keyword (non-editing mode) — used to register passive capture before opening the URL. */
  onSearchClick?: (k: string) => void | Promise<void>;
}) {
  const [newKw, setNewKw] = useState('');
  return (
    <div className="chip-row" style={{ marginTop: 6 }}>
      {keywords.map((k, i) => {
        const inner = <>{k}{editable && onRemove && <button className="chip-x" onClick={() => onRemove(i)} aria-label="Remove">×</button>}</>;
        if (!editable && searchHrefFor) {
          return (
            <a
              key={i}
              className="chip topic-chip"
              href={searchHrefFor(k)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onSearchClick ? (e) => { e.preventDefault(); void onSearchClick(k); } : undefined}
            >
              {inner}
            </a>
          );
        }
        return <span key={i} className={`chip${editable ? ' removable' : ''}`}>{inner}</span>;
      })}
      {editable && onAdd && (
        <span className="chip-add">
          <input
            value={newKw}
            onChange={e => setNewKw(e.target.value)}
            placeholder="+ keyword"
            onKeyDown={e => { if (e.key === 'Enter' && newKw.trim()) { onAdd(newKw.trim()); setNewKw(''); } }}
          />
        </span>
      )}
    </div>
  );
}
